// Client-side E2E encryption utilities (AES-GCM via WebCrypto)
// Key lives ONLY in the URL fragment. Server never sees plaintext or key.

const enc = new TextEncoder();
const dec = new TextDecoder();

// base64url helpers
export function toB64Url(bytes) {
  const bin = String.fromCharCode(...new Uint8Array(bytes));
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
export function fromB64Url(str) {
  const s = str.replace(/-/g, "+").replace(/_/g, "/");
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const bin = atob(s + pad);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

async function genKey() {
  return crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
}

async function exportRawKey(key) {
  const raw = await crypto.subtle.exportKey("raw", key);
  return new Uint8Array(raw);
}

async function importRawKey(rawBytes) {
  return crypto.subtle.importKey(
    "raw",
    rawBytes,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

/**
 * Encrypt plaintext, returning ciphertext, iv, keyString (base64url) for URL fragment.
 */
export async function encryptSecret(plaintext) {
  const key = await genKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    enc.encode(plaintext)
  );
  const rawKey = await exportRawKey(key);
  return {
    ciphertext: toB64Url(ct),
    iv: toB64Url(iv),
    key: toB64Url(rawKey),
  };
}

/**
 * Decrypt ciphertext with a key from URL fragment.
 */
export async function decryptSecret(ciphertextB64, ivB64, keyB64) {
  const key = await importRawKey(fromB64Url(keyB64));
  const iv = fromB64Url(ivB64);
  const ct = fromB64Url(ciphertextB64);
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
  return dec.decode(pt);
}
